import classNames from 'classnames';
import React, { HTMLAttributes, ReactNode } from 'react';

import styles from './Label.module.css';
import { TooltipIcon } from '../TooltipIcon';

export interface LabelProps extends Pick<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style'
> {
  /**
   * The label text
   */
  children: ReactNode;
  /**
   * Needed to determine if field is disabled or not.
   * Default value is false.
   */
  disabled?: boolean;
  /**
   * Defines the labeled input by providing its ID.
   *
   * **Note:** Can be used with both `Input` and native input.
   */
  for?: string;
  /**
   * Defines whether an asterisk character is added to the component text.
   *
   * **Note:** Usually indicates that user input (bound with the `for` property) is required. In that case the `required property of the corresponding input should also be set.`
   */
  required?: boolean;
  /**
   * Defines whether colon is added to the component text.
   *
   * **Note:** Usually used in forms.
   */
  showColon?: boolean;
  /**
   * Sets Tooltips text
   */
  tooltipText?: string;
}

export function Label({
  children,
  className,
  disabled,
  for: htmlFor,
  required,
  showColon,
  style,
  tooltipText,
}: LabelProps) {
  return (
    <div className={styles.container}>
      <label
        className={classNames('fd-form-label', styles.label, className, {
          [styles.disabled]: disabled,
          'fd-form-label--required': required,
          'fd-form-label--colon': showColon,
        })}
        htmlFor={htmlFor}
        style={style}
      >
        {children}
      </label>
      {tooltipText && (
        <span className={classNames(styles.icon)}>
          <TooltipIcon
            className={classNames(styles.info, disabled && styles['disabled-color'])}
            tooltipText={tooltipText}
          />
        </span>
      )}
    </div>
  );
}
